"use client";

import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { NavigationItem } from "@/navigation/types";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { useTheme } from "@/context/ThemeContext";
import {
  selectActiveSessionCompanyName,
  selectIntacctId,
} from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import logoMain from "@/assets/images/logo-main.svg";
import logoMainWhite from "@/assets/images/logo-main-white.svg";
import {
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ChevronRightIcon,
  ChevronUpDownIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import permissionsService from "@/services/permissionsService";
import { filterNavigationByAccess } from "@/utils/navigationHelpers";
import { RootState } from "@/app/rootReducer";
import CompanySwitcherModal from "@/components/CompanySwitcherModal/CompanySwitcherModal";
import { openFileManagerDialog } from "@/app/globalSlices/fileManagerDialogSlice";
import { cn } from "@/utils/utils";

interface MobileSideNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Animated Submenu Component
function AnimatedSubmenu({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  const [height, setHeight] = useState<number | undefined>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(isOpen ? contentHeight : 0);
    }
  }, [isOpen, children]);

  return (
    <div
      className="overflow-hidden transition-all duration-300 ease-in-out"
      style={{ height: height }}
    >
      <div ref={contentRef}>
        <SidebarMenuSub className="mt-1 space-y-0.5 animate-in slide-in-from-top-1 duration-200">
          {children}
        </SidebarMenuSub>
      </div>
    </div>
  );
}

export default function MobileSideNav({
  open,
  onOpenChange,
}: MobileSideNavProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const activeCompanyName = useSelector(selectActiveSessionCompanyName);
  const activeCompanyIntacctId = useSelector(selectIntacctId);

  // Pull in user app settings
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );
  const userName =
    userAppSettings?.firstName && userAppSettings?.lastName
      ? `${userAppSettings.firstName} ${userAppSettings.lastName}`
      : userAppSettings?.email || "User";

  // Theme / navigation
  const { theme } = useTheme();
  const rawNav = getUnifiedNavigation();
  const { hasPermission, hasRole, hasCertainPathCompany } =
    permissionsService();

  const canOpenCompanySwitcher =
    hasRole("ROLE_SUPER_ADMIN") ||
    hasRole("ROLE_COACH") ||
    hasRole("ROLE_MARKETING");

  const filteredNav = filterNavigationByAccess(
    rawNav,
    hasPermission,
    hasRole,
    hasCertainPathCompany(),
  );

  // State for menu toggling & submenus
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  // State for company switcher modal
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);

  // State for impersonation
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const impersonateEmployeeUuid = localStorage.getItem("impersonateEmployeeUuid");
    setIsImpersonating(!!impersonateEmployeeUuid);
  }, []);

  function handleToggleItem(itemKey: string) {
    setOpenItems((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  }

  function isActiveRoute(href: string): boolean {
    if (href === "#") return false;

    // Exact match for /hub to prevent it from matching all /hub/* routes
    if (href === "/hub") {
      return location.pathname === "/hub";
    }

    // For all other routes, check exact match or if it's a parent route
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  }

  const handleSignOut = () => {
    onOpenChange(false);
    navigate("/logout");
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem("selectedCompanyUuid");
    localStorage.removeItem("impersonateEmployeeUuid");
    onOpenChange(false);
    navigate(0);
  };

  function renderNavigation(items: NavigationItem[], depth = 0) {
    return items.map((item) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!openItems[itemKey];
      const isActive = item.href ? isActiveRoute(item.href) : false;

      if (hasChildren) {
        return (
          <SidebarMenuItem
            data-state={isOpen ? "open" : undefined}
            key={itemKey}
          >
            <SidebarMenuButton
              className={cn(
                depth === 0 && "font-medium",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              data-state={isOpen ? "open" : undefined}
              onClick={() => handleToggleItem(itemKey)}
              size={depth === 0 ? "lg" : "default"}
            >
              <div
                className={cn(
                  "flex h-5 w-5 items-center justify-center transition-transform duration-300",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg",
                  isOpen && "scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
              <ChevronRightIcon
                aria-hidden="true"
                className={cn(
                  "ml-auto h-4 w-4 transition-transform duration-300 ease-in-out",
                  isOpen && "rotate-90",
                )}
              />
            </SidebarMenuButton>
            <AnimatedSubmenu isOpen={isOpen}>
              {renderNavigation(item.children!, depth + 1)}
            </AnimatedSubmenu>
          </SidebarMenuItem>
        );
      }

      // Check if it's an external URL
      const isExternalUrl =
        item.href?.startsWith("http://") || item.href?.startsWith("https://");

      if (isExternalUrl) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              asChild
              className={cn(
                depth === 0 && "font-medium",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              size={depth === 0 ? "lg" : "default"}
            >
              <a
                href={item.href}
                onClick={() => onOpenChange(false)}
                rel="noopener noreferrer"
                target="_blank"
                title={item.name}
              >
                <div
                  className={cn(
                    "flex h-5 w-5 items-center justify-center transition-transform duration-200",
                    depth === 0 &&
                      "bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg",
                    "hover:scale-110",
                  )}
                >
                  <item.icon
                    className={`${depth === 0 ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`}
                  />
                </div>
                <span className="flex-1">{item.name}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      // Check if it's a dialog item
      if (item.isDialog) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                depth === 0 && "font-medium",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              onClick={() => {
                onOpenChange(false); // Close mobile nav
                if (item.dialogAction === "fileManager") {
                  dispatch(openFileManagerDialog());
                }
              }}
              size={depth === 0 ? "lg" : "default"}
            >
              <div
                className={cn(
                  "flex h-5 w-5 items-center justify-center transition-transform duration-200",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg",
                  "hover:scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      return (
        <SidebarMenuItem key={itemKey}>
          <SidebarMenuButton
            asChild
            className={cn(
              depth === 0 && "font-medium",
              isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              "transition-all duration-200",
            )}
            size={depth === 0 ? "lg" : "default"}
          >
            <Link
              onClick={() => onOpenChange(false)}
              title={item.name}
              to={item.href ?? "#"}
            >
              <div
                className={cn(
                  "flex h-5 w-5 items-center justify-center transition-transform duration-200",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg",
                  "hover:scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });
  }

  // Extract navigation sections directly from their children
  const hubSection = filteredNav.find((item) => item.internalName === "hub");
  const stochasticSection = filteredNav.find(
    (item) => item.internalName === "stochastic",
  );
  // For CertainPath companies, show "Internal Admin" (internalName: "admin")
  // For non-CertainPath companies, show "Admin" (internalName: "company-admin")
  const internalAdminSection = filteredNav.find(
    (item) => item.internalName === "admin",
  );
  const companyAdminSection = filteredNav.find(
    (item) => item.internalName === "company-admin",
  );
  // Use whichever admin section is available (filtered by access rules)
  const adminSection = internalAdminSection || companyAdminSection;

  // Get the direct children items
  const hubItems = hubSection?.children || [];
  const stochasticItems = stochasticSection?.children || [];
  const adminItems = adminSection?.children || [];

  return (
    <>
      <Sheet onOpenChange={onOpenChange} open={open}>
        <SheetContent
          className="h-screen w-screen p-0 m-0 bg-sidebar text-sidebar-foreground overflow-hidden flex flex-col"
          side="top"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Mobile Menu</SheetTitle>
            <SheetDescription>Slides in from the top</SheetDescription>
          </SheetHeader>

          <SidebarHeader className="border-b-2 border-sidebar-border/50 bg-gradient-to-b from-sidebar to-sidebar/95 flex-shrink-0">
            <div className="flex flex-col space-y-4 px-4 py-4">
              <img
                alt="Your Company"
                className="h-8 w-auto"
                src={theme === "dark" ? logoMainWhite : logoMain}
              />
              {canOpenCompanySwitcher ? (
                <button
                  className="group flex w-full items-center justify-between rounded-xl bg-sidebar-accent/50 p-3 text-left transition-all hover:bg-sidebar-accent hover:shadow-md"
                  onClick={() => {
                    setCompanySwitcherOpen(true);
                    onOpenChange(false);
                  }}
                  type="button"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 transition-transform duration-200 group-hover:scale-110">
                      <BuildingOfficeIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold leading-none">
                        {activeCompanyName || "No Company"}
                      </div>
                      {activeCompanyIntacctId && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          ID: {activeCompanyIntacctId}
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:scale-110"
                  />
                </button>
              ) : (
                <div className="flex items-center space-x-3 rounded-xl bg-sidebar-accent/30 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <BuildingOfficeIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <div className="text-sm font-semibold">
                      {activeCompanyName || "No Company"}
                    </div>
                    {activeCompanyIntacctId && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        ID: {activeCompanyIntacctId}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SidebarHeader>

          <SidebarContent className="px-2 overflow-y-auto flex-1">
            {hubItems.length > 0 && (
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                  <span>Hub</span>
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-1">
                    {renderNavigation(hubItems)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            {stochasticItems.length > 0 && (
              <>
                <SidebarSeparator className="my-2" />
                <SidebarGroup>
                  <SidebarGroupLabel className="flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="h-px flex-1 bg-sidebar-border/50" />
                    <span>Stochastic</span>
                    <div className="h-px flex-1 bg-sidebar-border/50" />
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu className="space-y-1">
                      {renderNavigation(stochasticItems)}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </>
            )}

            {adminItems.length > 0 && (
              <>
                <SidebarSeparator className="my-2" />
                <SidebarGroup>
                  <SidebarGroupLabel className="flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="h-px flex-1 bg-sidebar-border/50" />
                    <span>Administration</span>
                    <div className="h-px flex-1 bg-sidebar-border/50" />
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu className="space-y-1">
                      {renderNavigation(adminItems)}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </>
            )}
          </SidebarContent>

          <SidebarFooter className="border-t-2 border-sidebar-border/50 bg-gradient-to-t from-sidebar to-sidebar/95 p-2 flex-shrink-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <div className="flex items-center gap-3 px-2 py-2">
                  <UserCircleIcon className="h-5 w-5 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{userName}</span>
                    <span className="text-xs text-muted-foreground">
                      Logged in
                    </span>
                  </div>
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className="transition-all duration-200"
                >
                  <Link onClick={() => onOpenChange(false)} to="/hub/settings">
                    <Cog6ToothIcon className="h-5 w-5" />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isImpersonating && (
                <>
                  <SidebarSeparator className="my-1" />
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className="text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:hover:bg-orange-950/20 transition-all duration-200"
                      onClick={handleExitImpersonation}
                    >
                      <UserCircleIcon className="h-5 w-5" />
                      <span>Exit Impersonation</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/20 transition-all duration-200"
                  onClick={handleSignOut}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span>Sign Out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </SheetContent>
      </Sheet>

      {/* Company Switcher Modal */}
      <CompanySwitcherModal
        onClose={() => setCompanySwitcherOpen(false)}
        open={companySwitcherOpen}
      />
    </>
  );
}
